import {
	ArrowDropDown as ArrowDropDownIcon,
	ArrowDropUp as ArrowDropUpIcon,
	DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
	Download as DownloadIcon,
	FilterAlt as FilterAltIcon,
	List as ListIcon,
	LocationOnOutlined as LocationOnOutlinedIcon,
	PersonOutline as PersonOutlineIcon,
	RadioButtonChecked as RadioButtonCheckedIcon,
	Search as SearchIcon,
	Visibility as VisibilityIcon
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Card,
	CircularProgress,
	Grid2,
	IconButton,
	InputAdornment,
	Menu,
	MenuItem,
	Pagination,
	styled,
	TextField,
	Tooltip,
	tooltipClasses,
	TooltipProps,
	Typography
} from "@mui/material";
import moment from "moment";
import { ChangeEvent, FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";

import VehicleLogs from ".";
import TableFilterDialog from "../../components/TableFilterDialog";
import TableSearchInput from "../../components/TableSearchInput";
import { useAppSelector } from "../../redux";
import { useGetEnforcementSessionsQuery } from "../../redux/reducers/alerts.reducer";
import { EVENT_PRODUCT_TYPE_E, IDownloadLogsRequestData, IGetEnforcementSessionsRequestData } from "../../types";
import { ALERT_EVENT_LOGS_REQUEST_TIME_FORMAT } from "../../utils/constants";
import FullscreenVehicleDetails from "./FullscreenVehicleDetails";
import { useDownloadJob } from "../../hooks/useDownloadReport";

interface ISelectedSessionDetails {
	id: number;
	isLive: boolean;
	fullscreen: boolean;
}

const PAGE_SIZE = 6;

const SessionTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: theme.palette.primary.light,
		textAlign: "center",
		padding: "0.5rem",
		fontWeight: 500,
		maxWidth: "300px",
		color: theme.palette.primary.main,
		fontSize: "14px"
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.primary.light,
		width: "28px",
		height: "20px"
	},
	[`& .${tooltipClasses.tooltipPlacementTop} .${tooltipClasses.arrow}`]: {
		marginBottom: "-20px"
	}
}));

const SessionsList: FC = () => {
	const filtersState = useAppSelector((state) => state.filters);

	const [pageNumberInput, setPageNumberInput] = useState<string>("");
	// const [selectedDateRange, setSelectedDateRange] = useState<SelectedRangeType>("today");
	const [selectedSession, setSelectedSession] = useState<ISelectedSessionDetails | null>(null);
	const { isLoading: downloadButtonLoading, startDownload } = useDownloadJob();
	const [showTableFiltersDialog, setShowTableFiltersDialog] = useState<boolean>(false);
	const [statusMenuOpen, setStatusMenuOpen] = useState<boolean>(false);
	const statusMenuAnchorRef = useRef<HTMLButtonElement>(null);
	const [filters, setFilters] = useState<IGetEnforcementSessionsRequestData>({
		page: 1,
		session_start__gt: "",
		session_start__lt: "",
		page_size: PAGE_SIZE
	});

	// APIS
	// GET ENFORCEMENT SESSIONS
	const {
		data: sessionsResponse,
		isLoading: getEnforcementSessionLoading,
		isFetching: getEnforcementSessionFetching
	} = useGetEnforcementSessionsQuery(filters, { skip: !filters.session_start__gt || !filters.session_start__lt });

	const sessions = useMemo(() => sessionsResponse?.results ?? [], [sessionsResponse]);

	const pageCount = useMemo<number>(() => {
		const sessionsCount = sessionsResponse?.count ?? 0;
		const pageSize = filters.page_size ?? PAGE_SIZE;

		return Math.ceil(sessionsCount / pageSize);
	}, [filters.page_size, sessionsResponse?.count]);

	function handleChangePageNumberInput(event: ChangeEvent<HTMLInputElement>) {
		setPageNumberInput(event.target.value);
	}

	function handlePageChange(page: number) {
		setFilters((currentFilters) => ({ ...currentFilters, page }));
	}

	function handleSkipToPage(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (pageNumberInput) {
			handlePageChange(Number(pageNumberInput));
			setPageNumberInput("");
		}
	}

	function handleSelectSession(sessionId: number, isLive: boolean, fullscreen: boolean = false) {
		setSelectedSession({ id: sessionId, isLive: isLive, fullscreen: fullscreen });
	}

	function handleDownloadButtonClick() {
		const activeFilters: IDownloadLogsRequestData = {
			start_date: moment(filters.session_start__gt).format(ALERT_EVENT_LOGS_REQUEST_TIME_FORMAT),
			end_date: moment(filters.session_start__lt).format(ALERT_EVENT_LOGS_REQUEST_TIME_FORMAT)
		};

		startDownload({
			report_type: "alert_logs",
			report_format: "excel",
			job_meta_data: {
				filters: activeFilters
			}
		});
	}

	function handleOpenTableFiltersDialog() {
		setShowTableFiltersDialog(true);
	}

	function handleCloseTableFiltersDialog() {
		setShowTableFiltersDialog(false);
	}

	function handleChangeSelectedDevices(deviceIds: number[]) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (deviceIds.length === 0) delete updatedFilters.device;
			else updatedFilters.device = deviceIds;

			return updatedFilters;
		});

		handleCloseTableFiltersDialog();
	}

	function handleSubmitSearchInput(searchInput: string) {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (searchInput) updatedFilters.search = searchInput;
			else delete updatedFilters.search;

			return updatedFilters;
		});
	}

	function handleOpenStatusMenu() {
		setStatusMenuOpen(true);
	}

	function handleCloseStatusMenu() {
		setStatusMenuOpen(false);
	}

	function handleChangeSelectedStatus(statusValue: "all" | "pending" | "approved" | "rejected") {
		setFilters((currentFilters) => {
			const updatedFilters = { ...currentFilters, page: 1 };

			if (statusValue === "all") delete updatedFilters.validated_status;
			else updatedFilters.validated_status = statusValue;

			return updatedFilters;
		});
		handleCloseStatusMenu();
	}

	useEffect(() => {
		if (filtersState.timeFilter) {
			setFilters((currentFilters) => ({
				...currentFilters,
				page: 1,
				session_start__gt: filtersState.timeFilter.start_time,
				session_start__lt: filtersState.timeFilter.end_time
			}));
		}
	}, [filtersState]);

	if (selectedSession) {
		if (selectedSession.fullscreen) {
			return <FullscreenVehicleDetails sessionId={selectedSession.id} navigateBack={() => setSelectedSession(null)} />;
		}

		return (
			<VehicleLogs
				sessionId={selectedSession.id}
				isLive={selectedSession.isLive}
				navigateBack={() => setSelectedSession(null)}
			/>
		);
	}

	return (
		<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "1rem", height: 0 }}>
			<Box className="vehicle-logs-section-header-wrapper" sx={{ justifyContent: "flex-end", gap: "1rem" }}>
				<TableSearchInput
					showClearButton={!!filters.search}
					placeholder="Search by Interceptor ID, Officer..."
					onSubmit={handleSubmitSearchInput}
				/>

				<Button
					color={
						filters.validated_status === "pending"
							? "warning"
							: filters.validated_status === "approved"
							? "success"
							: filters.validated_status === "rejected"
							? "error"
							: "primary"
					}
					variant="contained"
					disableElevation
					ref={statusMenuAnchorRef}
					onClick={handleOpenStatusMenu}
					endIcon={statusMenuOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
					sx={{ minWidth: 120, paddingX: 2 }}
				>
					{filters.validated_status === "pending"
						? "Pending"
						: filters.validated_status === "approved"
						? "Challan Issued"
						: filters.validated_status === "rejected"
						? "Rejected"
						: "All Status"}
				</Button>

				<Menu
					anchorEl={statusMenuAnchorRef.current}
					open={statusMenuOpen}
					onClose={handleCloseStatusMenu}
					MenuListProps={{ "aria-labelledby": "status-filter-button" }}
					anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
					transformOrigin={{ horizontal: "center", vertical: "top" }}
				>
					<MenuItem
						onClick={() => handleChangeSelectedStatus("all")}
						classes={{ root: "session-status-filter-menu-item all" }}
					>
						All Status
					</MenuItem>

					<MenuItem
						onClick={() => handleChangeSelectedStatus("pending")}
						classes={{ root: "session-status-filter-menu-item pending" }}
					>
						Pending
					</MenuItem>

					<MenuItem
						onClick={() => handleChangeSelectedStatus("approved")}
						classes={{ root: "session-status-filter-menu-item approved" }}
					>
						Challan Issued
					</MenuItem>

					{/* <MenuItem
						onClick={() => handleChangeSelectedStatus("rejected")}
						classes={{ root: "session-status-filter-menu-item rejected" }}
					>
						Rejected
					</MenuItem> */}
				</Menu>

				<LoadingButton
					disableElevation
					variant="contained"
					color="primary"
					startIcon={<DownloadIcon />}
					onClick={handleDownloadButtonClick}
					loading={downloadButtonLoading}
					loadingPosition="start"
				>
					Download
				</LoadingButton>

				<IconButton color="primary" onClick={handleOpenTableFiltersDialog}>
					<FilterAltIcon />
				</IconButton>
			</Box>

			<Box className="vehicle-logs-table-wrapper" sx={{ flex: 1, overflow: "auto", p: 2, gap: "1rem" }}>
				<Grid2 container spacing={3} sx={{ flex: 1 }}>
					{getEnforcementSessionLoading || getEnforcementSessionFetching ? (
						<Box className="sessions-list-loader">
							<CircularProgress />
						</Box>
					) : sessions.length === 0 ? (
						<Box className="sessions-list-loader">
							<Typography variant="body1" sx={{ m: 2 }}>
								No sessions found.
							</Typography>
						</Box>
					) : (
						sessions.map((session) => {
							return (
								<Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={session.id}>
									<Card elevation={0} classes={{ root: "session-card-root" }}>
										{/* Date and Session # */}
										<Box className="session-card-header">
											<Box className="session-card-header-date-wrapper">
												<Typography variant="h6" marginLeft="0.25rem">
													{moment(session.session_start).format("DD MMMM")}
												</Typography>

												<SessionTooltip arrow placement="top" title={`Session UUID: ${session.uuid}`}>
													<Box className="session-card-header-session-id">
														<Typography>Session #{session.id}</Typography>
													</Box>
												</SessionTooltip>
											</Box>

											{session.is_ongoing ? (
												<Box className={`session-card-header-status ongoing`}>
													<RadioButtonCheckedIcon fontSize="small" sx={{ color: "var(--color-error-main)" }} />
													<Typography variant="body2">Ongoing</Typography>
												</Box>
											) : (
												<Box
													className={`session-card-header-status ${
														session.validated_status?.toLowerCase().replace(/ /g, "-") || "pending"
													}`}
												>
													<Typography variant="body2">
														{session.validated_status === "approved"
															? "Challan Issued"
															: session.validated_status || "pending"}
													</Typography>
												</Box>
											)}
										</Box>

										{/* Officer */}
										<Box className="session-card-details-wrapper">
											<PersonOutlineIcon sx={{ color: "#A0A4AB" }} />

											<Box className="session-card-details-text">
												<Typography color="#A0A4AB" lineHeight={1}>
													Officer
												</Typography>

												<Typography fontSize="18px" color="#4B5563" fontWeight={500} lineHeight={1.2}>
													{`${session.officer_first_name} ${session.officer_last_name}`.trim() ||
														session.officer_username}
												</Typography>
											</Box>
										</Box>

										{/* Location */}
										<Box className="session-card-details-wrapper">
											<LocationOnOutlinedIcon sx={{ color: "#A0A4AB" }} />

											<Box className="session-card-details-text">
												<Typography color="#A0A4AB" lineHeight={1}>
													Location
												</Typography>

												<Typography fontSize="18px" color="#4B5563" fontWeight={500} lineHeight={1.2}>
													{session.location_name}
												</Typography>

												<Typography color="#6B7280" fontWeight={400} fontSize="16px" lineHeight={1}>
													{session.latitude}, {session.longitude}
												</Typography>
											</Box>
										</Box>

										{/* Interceptor ID */}
										<Box className="session-card-details-wrapper">
											<DirectionsCarFilledOutlinedIcon sx={{ color: "#A0A4AB" }} />

											<Box className="session-card-details-text">
												<Typography color="#A0A4AB" lineHeight={1}>
													Interceptor ID
												</Typography>

												<Typography fontSize="18px" color="#4B5563" fontWeight={500} lineHeight={1.2}>
													{session.interceptor_numberplate}
												</Typography>
											</Box>
										</Box>

										{/* Footer */}
										<Box className="session-card-footer">
											<Button
												disableRipple
												disableElevation
												variant="text"
												startIcon={<ListIcon />}
												onClick={() => handleSelectSession(session.id, session.is_ongoing)}
											>
												List View
											</Button>
											<Button
												disableRipple
												disableElevation
												variant="text"
												startIcon={<VisibilityIcon />}
												onClick={() => handleSelectSession(session.id, session.is_ongoing, true)}
											>
												View Details
											</Button>
										</Box>
									</Card>
								</Grid2>
							);
						})
					)}
				</Grid2>

				{/* Pagination */}
				{(sessionsResponse?.count ?? 0) > PAGE_SIZE ? (
					<Box className="sessions-list-pagination-wrapper">
						<Pagination
							color="primary"
							count={pageCount}
							page={filters.page}
							onChange={(_event, newPage) => handlePageChange(newPage)}
						/>

						<Box sx={{ width: "200px" }} component="form" noValidate onSubmit={handleSkipToPage}>
							<TextField
								fullWidth
								placeholder="Page"
								size="small"
								autoComplete="off"
								value={pageNumberInput}
								onChange={handleChangePageNumberInput}
								slotProps={{
									input: {
										endAdornment: (
											<InputAdornment position="end">
												<IconButton edge="end" type="submit">
													<SearchIcon fontSize="small" />
												</IconButton>
											</InputAdornment>
										)
									}
								}}
							/>
						</Box>
					</Box>
				) : null}
			</Box>

			<TableFilterDialog
				open={showTableFiltersDialog}
				defaultValue={filters.device}
				productType={EVENT_PRODUCT_TYPE_E.RECON}
				onClose={handleCloseTableFiltersDialog}
				onChange={handleChangeSelectedDevices}
			/>
		</Box>
	);
};

export default SessionsList;
